/**
 * Compact "alarm sound" picker for the customer app.
 * Lets a customer preview and choose which admin-published sound plays
 * for a given category (medicine, vaccine, feed, …).
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { custApi, CUST_API_BASE } from "../../lib/customerApi";
import {
  MusicNote, Play, Pause, CheckCircle, CaretDown, Star,
} from "@phosphor-icons/react";

export default function AlarmSoundPicker({ category, label, lovedOneId }) {
  const [open, setOpen] = useState(false);
  const [sounds, setSounds] = useState([]);
  const [prefId, setPrefId] = useState("");
  const [current, setCurrent] = useState(null);   // resolved sound (pref → default → any)
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const curPath = lovedOneId
        ? `/me/alarm-sound-for/${category}?loved_one_id=${encodeURIComponent(lovedOneId)}`
        : `/me/alarm-sound-for/${category}`;
      const [listR, prefR, curR] = await Promise.all([
        custApi.get(`/me/alarm-sounds?category=${category}`).catch(() => ({ data: {} })),
        custApi.get("/me/preferences/alarm-sound").catch(() => ({ data: {} })),
        custApi.get(curPath).catch(() => ({ data: {} })),
      ]);
      setSounds(listR.data?.sounds || []);
      const flatPref = (prefR.data?.preferences || {})[category] || "";
      const loPref = lovedOneId
        ? ((prefR.data?.by_loved_one || {})[lovedOneId] || {})[category] || ""
        : "";
      setPrefId(lovedOneId ? (loPref || "") : flatPref);
      setCurrent(curR.data?.sound || null);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, lovedOneId]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setPlayingId(null);
  };
  const play = (row) => {
    stopAudio();
    const url = row.url.startsWith("/")
      ? `${CUST_API_BASE.replace(/\/api$/, "")}${row.url}`
      : row.url;
    const audio = new Audio(url);
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => { setPlayingId(null); toast.error("Could not play"); };
    audio.play().catch(() => toast.error("Tap again to allow audio"));
    audioRef.current = audio;
    setPlayingId(row.id);
  };
  useEffect(() => () => stopAudio(), []);

  const choose = async (row) => {
    setSaving(true);
    try {
      const body = lovedOneId
        ? { by_loved_one: { [lovedOneId]: { [category]: row.id } } }
        : { preferences: { [category]: row.id } };
      await custApi.patch("/me/preferences/alarm-sound", body);
      setPrefId(row.id);
      setCurrent(row);
      toast.success(`"${row.name}" set as ${lovedOneId ? "profile" : "default"} alarm`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (sounds.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[#E5E2D9] bg-white overflow-hidden" data-testid={`alarm-picker-${category}`}>
      <button onClick={() => setOpen(v => !v)} type="button"
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#FEF7F6] transition text-left">
        <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
          <MusicNote size={18} weight="fill"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-[#111827] leading-tight">{label || "Alarm sound"}</div>
          <div className="text-[11px] text-[#5C5A55] truncate">
            {current?.name || "Default (tenant-selected)"}
          </div>
        </div>
        <CaretDown size={12} weight="bold" className={`text-[#8A8780] transition ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="border-t border-[#F0EEE7] divide-y divide-[#F5F4EE]">
          {sounds.map(s => {
            const selected = s.id === prefId;
            const isCurrent = current?.id === s.id;
            return (
              <div key={s.id} className={`flex items-center gap-3 px-4 py-2.5 ${selected ? "bg-[#FEF7F6]" : ""}`}
                   data-testid={`alarm-choose-${s.id}`}>
                <button onClick={() => playingId === s.id ? stopAudio() : play(s)}
                  className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center ${
                    playingId === s.id ? "bg-[#DC2626] text-white" : "bg-[#FEE2E2] text-[#DC2626]"
                  }`}>
                  {playingId === s.id ? <Pause size={14} weight="fill"/> : <Play size={14} weight="fill"/>}
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-[#111827] flex items-center gap-1.5">
                    {s.name}
                    {s.is_default && !prefId && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#FFF8E6] text-[#7A5A11] font-bold">
                        <Star size={9} weight="fill"/> default
                      </span>
                    )}
                    {isCurrent && (
                      <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780]">now playing on alarms</span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#8A8780]">
                    {(s.language || "").toUpperCase() || "—"}{s.gender_hint ? ` · ${s.gender_hint}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => choose(s)}
                  disabled={selected || saving}
                  className={`h-8 px-2.5 rounded-md text-[11px] font-bold inline-flex items-center gap-1 ${
                    selected
                      ? "bg-[#EEF7EC] text-[#166534]"
                      : "border border-[#E5E2D9] hover:bg-[#F7F6F2] text-[#111827]"
                  }`}>
                  {selected ? <><CheckCircle size={11} weight="fill"/> Selected</> : "Select"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
